import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Typography,
  Button,
  Container,
  Paper,
  Box,
  CircularProgress,
  useTheme,
  Fade,
  Stack,
  Link,
  Divider,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import HotelIcon from "@mui/icons-material/Hotel";
import BuildIcon from "@mui/icons-material/Build";
import CreditCardIcon from "@mui/icons-material/CreditCard"; // Icon for subscription
import { supabase } from "../../utils/supabaseClient";

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false); // State for the new button

  useEffect(() => {
    if (window.gtag) window.gtag("event", "paywall_converted");

    const init = async () => {
      // Poll for session — Stripe redirect can race with Supabase session restore
      let session = null;
      for (let i = 0; i < 10; i++) {
        const { data } = await supabase.auth.getSession();
        if (data?.session?.user) {
          session = data.session;
          break;
        }
        await new Promise((res) => setTimeout(res, 500));
      }
      setUser(session?.user || null);
      setLoading(false);
    };

    init();
  }, []);

  const handleContinue = () => {
    navigate("/");
  };

  // 💳 New Handler: Open Stripe Portal
  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "create-customer-portal"
      );

      if (error) throw error;

      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("Portal URL not received.");
      }
    } catch (error) {
      console.error("Error opening portal:", error);
      alert("Could not open subscription management settings.");
    } finally {
      setPortalLoading(false);
    }
  };

  const benefits = [
    {
      text: "Unlimited Searches",
      icon: <RocketLaunchIcon sx={{ color: "#7e78d2" }} />,
    },
    {
      text: "AI Review Analyzer",
      icon: <AutoAwesomeIcon sx={{ color: "#7e78d2" }} />,
    },
    {
      text: "Advanced Filters",
      icon: <FilterAltIcon sx={{ color: "#7e78d2" }} />,
    },
    {
      text: "Multi-Currency Support",
      icon: <CurrencyExchangeIcon sx={{ color: "#7e78d2" }} />,
    },
    {
      text: "Hotel Booking Options",
      icon: <HotelIcon sx={{ color: "#7e78d2" }} />,
    },
    { text: "Trip Helper AI", icon: <BuildIcon sx={{ color: "#7e78d2" }} /> },
  ];

  return (
    <>
    <Helmet>
      <meta name="robots" content="noindex, nofollow" />
      <title>Payment Success | FlyBnB</title>
    </Helmet>
    <Box
      sx={{
        minHeight: "90vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: 4,
        background:
          theme.palette.mode === "dark"
            ? "radial-gradient(circle at 50% 50%, #2a2a35 0%, #121212 100%)"
            : "radial-gradient(circle at 50% 50%, #f5f5f7 0%, #fff 100%)",
      }}
    >
      <Container maxWidth="sm">
        <Fade in={true} timeout={800}>
          <Paper
            elevation={24}
            sx={{
              p: { xs: 4, sm: 6 },
              textAlign: "center",
              borderRadius: 4,
              background:
                theme.palette.mode === "dark"
                  ? "rgba(30, 30, 30, 0.8)"
                  : "rgba(255, 255, 255, 0.9)",
              backdropFilter: "blur(20px)",
              border: `1px solid ${theme.palette.divider}`,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <CheckCircleRoundedIcon
              sx={{
                fontSize: 90,
                color: "#4caf50",
                mb: 3,
                filter: "drop-shadow(0px 10px 20px rgba(76, 175, 80, 0.3))",
              }}
            />

            <Typography
              variant="h3"
              component="h1"
              fontWeight="800"
              gutterBottom
              sx={{ letterSpacing: "-0.5px" }}
            >
              Welcome to Pro!
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 5, fontSize: "1.1rem", lineHeight: 1.6 }}
            >
              Your subscription is active. You now have full access to all
              exclusive FlyBnB features.
            </Typography>

            <Typography
              variant="subtitle1"
              fontWeight="700"
              sx={{ mb: 2, textAlign: "left", color: "text.primary" }}
            >
              You now have access to:
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
                textAlign: "left",
                mb: 5,
                bgcolor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.03)"
                    : "rgba(0,0,0,0.02)",
                p: 3,
                borderRadius: 3,
                border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`,
              }}
            >
              {benefits.map((item, index) => (
                <Stack
                  key={index}
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                >
                  {item.icon}
                  <Typography
                    variant="body2"
                    fontWeight="600"
                    color="text.primary"
                  >
                    {item.text}
                  </Typography>
                </Stack>
              ))}
            </Box>

            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <CircularProgress
                  size={28}
                  thickness={5}
                  sx={{ color: theme.palette.primary.main }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ animation: "pulse 1.5s infinite" }}
                >
                  Finalizing your account setup...
                </Typography>
              </Box>
            ) : (
              <Stack spacing={2}>
                {/* Primary Action */}
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleContinue}
                  fullWidth
                  sx={{
                    py: 2,
                    fontSize: "1.1rem",
                    fontWeight: "bold",
                    borderRadius: 3,
                    color: "#fff",
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    boxShadow: "0 8px 30px rgba(95, 90, 162, 0.3)",
                    textTransform: "none",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 12px 40px rgba(95, 90, 162, 0.4)",
                    },
                  }}
                >
                  Start Searching ✈️
                </Button>

                {/* 💳 Manage Subscription Button */}
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleManageSubscription}
                  disabled={portalLoading}
                  startIcon={
                    portalLoading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <CreditCardIcon />
                    )
                  }
                  sx={{
                    py: 1.5,
                    fontSize: "0.95rem",
                    fontWeight: "600",
                    borderRadius: 3,
                    textTransform: "none",
                    color: "text.secondary",
                    borderColor: theme.palette.divider,
                    "&:hover": {
                      borderColor: theme.palette.text.primary,
                      color: theme.palette.text.primary,
                      bgcolor: "transparent",
                    },
                  }}
                >
                  {portalLoading ? "Loading Portal..." : "Manage Subscription"}
                </Button>

                <Divider sx={{ my: 2 }} />

                {/* 📧 Contact Info */}
                <Typography variant="body2" color="text.secondary">
                  Questions or concerns? <br />
                  <Link
                    href="mailto:info@flybnb.io"
                    color="primary"
                    underline="hover"
                    fontWeight="bold"
                  >
                    info@flybnb.io
                  </Link>
                </Typography>
              </Stack>
            )}
          </Paper>
        </Fade>
      </Container>
    </Box>
    </>
  );
};

export default PaymentSuccess;
